'use client';

import { useCallback, useMemo, useState } from 'react';

import type { ViewerImage } from './types';

/**
 * Drives the "which image + is the lightbox open" state for gallery / trigger
 * surfaces. View-transform state (zoom, pan, fullscreen) is owned by
 * `ImageLightbox` itself, so this hook stays a small, reusable index machine.
 */
export function useImageViewer(images: ViewerImage[] = []) {
  const [index, setIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const safeIndex = useMemo(
    () => (images.length === 0 ? 0 : Math.min(index, images.length - 1)),
    [index, images.length],
  );

  const goTo = useCallback(
    (next: number) => {
      if (images.length === 0) return;
      setIndex(Math.max(0, Math.min(next, images.length - 1)));
    },
    [images.length],
  );

  const openAt = useCallback(
    (next: number) => {
      goTo(next);
      setIsOpen(true);
    },
    [goTo],
  );

  const close = useCallback(() => setIsOpen(false), []);
  const next = useCallback(() => goTo(safeIndex + 1), [goTo, safeIndex]);
  const previous = useCallback(() => goTo(safeIndex - 1), [goTo, safeIndex]);

  return {
    index: safeIndex,
    isOpen,
    currentImage: images[safeIndex] ?? null,
    hasPrevious: safeIndex > 0,
    hasNext: safeIndex < images.length - 1,
    openAt,
    close,
    setIsOpen,
    goTo,
    next,
    previous,
  };
}

export type UseImageViewerReturn = ReturnType<typeof useImageViewer>;
